/**
 * sdkBiddingMapping.js - `getconfig/pubsetting` -> unit-to-SDK-bidding-buyer mapping. Browser
 * twin of proxy-server/sdkBidding/pubsettingMapping.js (pinned by sdkBiddingParity.test.js).
 * Fallback only: when the proxy answers, render its answer.
 *
 * Derived record: validData.sdkBiddingMapping is stamped at capture time by the proxy, so it
 * survives body stripping; harToRows recomputes it from the HAR's own pubsetting row.
 * Contract: docs/sdlc/sdk-bidding-signoff/20-requirements.md ACs 1-10, 68-71. Never throws.
 */

const LIFTOFF_WATERFALL_SOURCE = '4260873306809781910';
const PUBSETTING_PATH = '/getconfig/pubsetting';

// Copy of proxy mediationNetworks.js adapterClasses (every sdkBidding network). The parity
// test compares this to the registry, so a new buyer there fails here until it is mirrored.
export const BUYER_ADAPTER_CLASSES = {
  liftoff: ['com.google.ads.mediation.vungle.VungleMediationAdapter', 'GADMediationAdapterVungle'],
  applovin: ['com.google.ads.mediation.applovin.AppLovinMediationAdapter'],
  meta: [
    'com.google.ads.mediation.facebook.FacebookMediationAdapter',
    'com.google.ads.mediation.facebook.FacebookAdapter',
    'GADMediationAdapterFacebook',
  ],
  mintegral: ['com.google.ads.mediation.mintegral.MintegralMediationAdapter'],
  pangle: ['com.google.ads.mediation.pangle.PangleMediationAdapter'],
  unity: ['com.google.ads.mediation.unity.UnityMediationAdapter'],
  inmobi: ['com.google.ads.mediation.inmobi.InMobiMediationAdapter'],
  dt_exchange: [
    'com.google.ads.mediation.fyber.FyberMediationAdapter',
    'com.google.ads.mediation.dtexchange.DTExchangeMediationAdapter',
  ],
  bidmachine: ['com.google.ads.mediation.bidmachine.BidMachineMediationAdapter'],
  moloco: ['com.google.ads.mediation.moloco.MolocoMediationAdapter'],
  ly_ads: ['com.google.ads.mediation.line.LineMediationAdapter'],
};

const ADAPTER_TO_BUYER = {};
Object.keys(BUYER_ADAPTER_CLASSES).forEach((id) => {
  BUYER_ADAPTER_CLASSES[id].forEach((cls) => { ADAPTER_TO_BUYER[cls] = id; });
});

const has = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k);
const buyerOfAdapter = (cls) => (has(ADAPTER_TO_BUYER, cls) ? ADAPTER_TO_BUYER[cls] : null);

function queryParam(url, name) {
  const m = new RegExp(`[?&]${name}=([^&#]*)`).exec(String(url || ''));
  if (!m) return null;
  try { return decodeURIComponent(m[1]); } catch { return m[1]; }
}

function platformOfUrl(url) {
  const js = queryParam(url, 'js') || '';
  if (/^afma-sdk-i-/i.test(js)) return 'ios';
  if (/^afma-sdk-a-/i.test(js)) return 'android';
  return null;
}

/** GAM `msid` (app bundle id) off a google ad request URL, or null. Never `an`. */
export function msidFromUrl(url) {
  return queryParam(url, 'msid');
}

export function metaAppId(placementId) {
  const s = String(placementId || '');
  const i = s.indexOf('_');
  return i > 0 ? s.slice(0, i) : null;
}

export function parsePubsettingMapping(input = {}) {
  try {
    if (input.captureSource === 'logcat') return { source: null, reason: 'usb' };
    let parsed = input.body;
    if (parsed === undefined || parsed === null || parsed === '') return { source: null, reason: 'unreadable' };
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { return { source: null, reason: 'malformed' }; }
    }
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.ad_unit_settings)) {
      return { source: null, reason: 'malformed' };
    }
    const units = {};
    for (const u of parsed.ad_unit_settings) {
      const id = u && typeof u.ad_unit_id === 'string' ? u.ad_unit_id : null;
      if (!id) continue;
      const nets = u.mediation_config && Array.isArray(u.mediation_config.ad_networks) ? u.mediation_config.ad_networks : [];
      const entries = [];
      for (const net of nets) {
        if (!net || typeof net !== 'object') continue;
        if (String(net.ad_source_external_id || '') === LIFTOFF_WATERFALL_SOURCE) continue;
        const adapters = Array.isArray(net.rtb_adapters) ? net.rtb_adapters : [];
        const data = net.data && typeof net.data === 'object' ? net.data : {};
        const placementId = data.placementID || data.placement_id || null;
        for (const cls of adapters) {
          const buyer = buyerOfAdapter(cls);
          if (!buyer) continue;
          if (entries.some((e) => e.buyer === buyer && e.adapterClass === cls && e.placementId === placementId)) continue;
          entries.push({ buyer, adapterClass: cls, placementId, appId: buyer === 'meta' ? metaAppId(placementId) : null });
        }
      }
      if (entries.length) units[id] = entries;
    }
    return {
      source: 'pubsetting',
      capturedAt: input.capturedAt || new Date().toISOString(),
      appName: queryParam(input.url, 'app_name'),
      platform: platformOfUrl(input.url),
      unitCount: parsed.ad_unit_settings.filter((u) => u && typeof u.ad_unit_id === 'string').length,
      units,
    };
  } catch {
    return { source: null, reason: 'malformed' };
  }
}

export function mappingToRecord(mapping) {
  if (!mapping || mapping.source !== 'pubsetting') return { source: null, reason: (mapping && mapping.reason) || 'malformed' };
  const units = {}; const placements = {}; const adapters = {}; const placementIds = {};
  Object.keys(mapping.units).forEach((id) => {
    units[id] = mapping.units[id].map((e) => e.buyer);
    mapping.units[id].forEach((e) => {
      if (e.placementId) {
        placements[id] = placements[id] || {}; placements[id][e.buyer] = e.placementId;
        // `placements` is last-write-wins; `placementIds` keeps every distinct id in wire order (R3).
        placementIds[id] = placementIds[id] || {};
        placementIds[id][e.buyer] = placementIds[id][e.buyer] || [];
        if (!placementIds[id][e.buyer].includes(e.placementId)) placementIds[id][e.buyer].push(e.placementId);
      }
      adapters[e.buyer] = adapters[e.buyer] || [];
      if (!adapters[e.buyer].includes(e.adapterClass)) adapters[e.buyer].push(e.adapterClass);
    });
  });
  return {
    source: 'pubsetting',
    appName: mapping.appName,
    capturedAt: mapping.capturedAt,
    platform: mapping.platform,
    unitCount: mapping.unitCount,
    units,
    placements,
    placementIds,
    adapters,
  };
}

export function deriveMappingRecord(input) {
  return mappingToRecord(parsePubsettingMapping(input));
}

export function recordToMapping(record) {
  if (!record || typeof record !== 'object') return { source: null, reason: 'unreadable' };
  if (record.source !== 'pubsetting') return { source: null, reason: record.reason || 'unreadable' };
  const units = {};
  const placements = record.placements || {};
  const adapters = record.adapters || {};
  const placementIds = record.placementIds && typeof record.placementIds === 'object' ? record.placementIds : null;
  Object.keys(record.units || {}).forEach((id) => {
    const entryOf = (buyer, placementId) => ({
      buyer,
      adapterClass: has(adapters, buyer) && adapters[buyer].length ? adapters[buyer][0] : null,
      placementId,
      appId: buyer === 'meta' ? metaAppId(placementId) : null,
    });
    const scalar = (buyer) => (has(placements, id) && has(placements[id], buyer) ? placements[id][buyer] : null);
    if (!placementIds || !has(placementIds, id)) {
      // Record stamped before placementIds existed: one entry per listed buyer, scalar placement.
      units[id] = (record.units[id] || []).map((buyer) => entryOf(buyer, scalar(buyer)));
      return;
    }
    // One entry per distinct placement id per buyer, in wire order (R3).
    const out = []; const done = new Set();
    (record.units[id] || []).forEach((buyer) => {
      const ids = has(placementIds[id], buyer) && Array.isArray(placementIds[id][buyer]) ? placementIds[id][buyer] : [];
      if (!ids.length) { out.push(entryOf(buyer, scalar(buyer))); return; }
      if (done.has(buyer)) return;
      done.add(buyer);
      ids.forEach((pid) => out.push(entryOf(buyer, pid)));
    });
    units[id] = out;
  });
  return {
    source: 'pubsetting',
    capturedAt: record.capturedAt || null,
    appName: record.appName == null ? null : record.appName,
    platform: record.platform == null ? null : record.platform,
    unitCount: record.unitCount == null ? Object.keys(units).length : record.unitCount,
    units,
  };
}

export function isPubsettingRow(row) {
  if (!row || typeof row !== 'object') return false;
  return row.requestType === 'publisher_settings' || String(row.url || '').includes(PUBSETTING_PATH);
}

const hasBody = (body) => (typeof body === 'string' ? body.length > 0 : !!(body && typeof body === 'object'));

export function mappingForRow(row) {
  try {
    if (row && row.captureSource === 'logcat') return { source: null, reason: 'usb' };
    const vd = row && row.validData && typeof row.validData === 'object' ? row.validData : null;
    const rec = vd && vd.sdkBiddingMapping;
    if (rec && rec.source === 'pubsetting') return recordToMapping(rec);
    if (hasBody(row && row.responseBody)) {
      const m = parsePubsettingMapping({ url: row.url, body: row.responseBody, captureSource: row.captureSource, capturedAt: (rec && rec.capturedAt) || row.timestamp || row.startedAtISO || row.ts || row.startedAt || undefined });
      if (m.source) return m;
      if (m.reason === 'malformed') return m;
    }
    if (rec && rec.reason) return { source: null, reason: rec.reason };
    return { source: null, reason: 'unreadable' };
  } catch {
    return { source: null, reason: 'unreadable' };
  }
}

/** Stamp / refresh validData.sdkBiddingMapping from the row's own body (HAR load). Mutates + returns row. */
export function stampMappingRecord(row) {
  try {
    if (!isPubsettingRow(row) || row.captureSource === 'logcat') return row;
    if (!hasBody(row.responseBody)) return row;
    const rec = deriveMappingRecord({ url: row.url, body: row.responseBody, captureSource: row.captureSource, capturedAt: row.timestamp || row.startedAtISO || row.ts || row.startedAt || undefined });
    if (rec.source === null && rec.reason === 'unreadable') return row;
    const vd = row.validData && typeof row.validData === 'object' ? row.validData : {};
    row.validData = { ...vd, sdkBiddingMapping: rec };
    return row;
  } catch {
    return row;
  }
}

const capturedKey = (m) => {
  const t = typeof m.capturedAt === 'number' ? m.capturedAt : Date.parse(m.capturedAt);
  return Number.isFinite(t) ? t : 0;
};

export function resolveSessionMapping(rows) {
  try {
    const list = Array.isArray(rows) ? rows : [];
    const byApp = {};
    let unreadable = false; let malformed = false;
    for (const r of list) {
      if (!isPubsettingRow(r) || (r && r.captureSource === 'logcat')) continue;
      const m = mappingForRow(r);
      if (m.source !== 'pubsetting') {
        if (m.reason === 'malformed') malformed = true; else unreadable = true;
        continue;
      }
      const key = m.appName == null ? '(unknown)' : m.appName;
      if (!has(byApp, key) || capturedKey(m) >= capturedKey(byApp[key])) byApp[key] = m;
    }
    const appNames = Object.keys(byApp);
    if (appNames.length) return { source: 'pubsetting', byApp, appNames };
    if (unreadable) return { source: null, reason: 'unreadable' };
    if (malformed) return { source: null, reason: 'malformed' };
    if (list.length && list.every((r) => r && r.captureSource === 'logcat')) return { source: null, reason: 'usb' };
    return { source: null, reason: 'not_captured' };
  } catch {
    return { source: null, reason: 'not_captured' };
  }
}

export function selectMapping(session, msid) {
  if (!session || session.source !== 'pubsetting') {
    return { mapping: null, selection: null, reason: (session && session.reason) || 'not_captured' };
  }
  if (msid && has(session.byApp, msid)) return { mapping: session.byApp[msid], selection: 'exact' };
  if (session.appNames.length === 1) return { mapping: session.byApp[session.appNames[0]], selection: 'single' };
  return { mapping: null, selection: null, reason: 'ambiguous' };
}

export function unitEntries(mapping, iu) {
  if (!mapping || mapping.source !== 'pubsetting' || !iu) return [];
  const out = [];
  for (const id of Object.keys(mapping.units)) {
    if (id === iu || iu.startsWith(`${id}/`)) out.push(...mapping.units[id]);
  }
  return out;
}
